import type { Breakpoint, MarginRule, PaddingRule } from '../../sanity/sanity.types';

/**
 * @todo When we upgrade Tailwind we'll be able to derive this from our config.
 *       At the moment, our Tailwind config can't be imported into files under
 *       `sanity/` because it's a CommonJS module (which is required for v2).
 */
export const BREAKPOINTS = {
  xs: '576px',
  sm: '768px',
  md: '992px',
  lg: '1280px',
  xl: '1440px',
} as const;

export const BREAKPOINT_OPTIONS = [
  { title: 'Extra Small', value: 'xs' },
  { title: 'Small', value: 'sm' },
  { title: 'Medium', value: 'md' },
  { title: 'Large', value: 'lg' },
  { title: 'Extra Large', value: 'xl' },
] as const;

export const SPACING_AXES = ['t', 'b', 'y'] as const;

export const SPACING_AXIS_OPTIONS = [
  { title: 'Top', value: 't' },
  { title: 'Bottom', value: 'b' },
  { title: 'Top & Bottom', value: 'y' },
] as const;

export const SPACING_SCALE = {
  none: 0,
  xxs: 8,
  xs: 16,
  sm: 32,
  md: 64,
  lg: 96,
  xl: 128,
  xxl: 256,
} as const;

export const SPACING_OPTIONS = [
  { title: `None (0px)`, value: 'none' },
  { title: 'XXS (8px)', value: 'xxs' },
  { title: 'XS (16px)', value: 'xs' },
  { title: 'Small (32px)', value: 'sm' },
  { title: 'Medium (64px)', value: 'md' },
  { title: 'Large (96px)', value: 'lg' },
  { title: 'XL (128px)', value: 'xl' },
  { title: 'XXL (256px)', value: 'xxl' },
] as const;

export type PresentationRule = MarginRule | PaddingRule;

export const getBreakpointPrefix = (breakpoint: Breakpoint) => {
  const { mode = 'all', screen } = breakpoint;

  if (mode === 'all') {
    return '';
  }

  let prefix = `${screen}`;

  // Note: XL is implicitly "XL only" already since it's the last
  // breakpoint in our screens config, so we don't need to append
  // the suffix to it
  if (mode === 'only' && screen !== 'xl') {
    prefix += '-only';
  }

  return prefix + ':';
};

export const buildMarginRule = (rule: MarginRule) => `${getBreakpointPrefix(rule.breakpoint)}m${rule.axis}-${SPACING_SCALE[rule.size]}`;

export const buildPaddingRule = (rule: PaddingRule) => `${getBreakpointPrefix(rule.breakpoint)}p${rule.axis}-${SPACING_SCALE[rule.size]}`;

export const buildPresentationClass = (rule: PresentationRule) => {
  switch (rule._type) {
    case 'marginRule':
      return buildMarginRule(rule);
    case 'paddingRule':
      return buildPaddingRule(rule);
    default:
      throw new Error(`Cannot build class name from unexpected block type: ${(rule as any)._type}`);
  }
};
